import React from 'react';
import { Box, Typography, Paper, Tooltip } from '@mui/material';
import type { SquareWithNumbers, ThemeData } from '../../types';
import { renderIcon } from './gridUtils';

/**
 * Determine if a square is clickable based on board state and user permissions.
 */
export const isSquareClickable = (
  square: SquareWithNumbers | undefined,
  boardStatus: string,
  isManager: boolean,
  currentUserId: string | undefined,
  assignModeEnabled: boolean,
  assignModePlayerId: string | undefined,
  assignModeAtLimit: boolean,
  approvedSquares: number | undefined,
  userClaimedCount: number,
  onSquareClick: ((row: number, col: number) => void) | undefined,
  onClearSquare: ((row: number, col: number) => Promise<void>) | undefined,
): boolean => {
  const isOwnSquare = square?.ownerId === currentUserId;
  const canClaimMore = approvedSquares === undefined || userClaimedCount < approvedSquares;
  const isSelectedPlayersSquare = assignModeEnabled && assignModePlayerId && (
    square?.ownerId === assignModePlayerId || square?.offlinePlayerId === assignModePlayerId
  );
  const isEmptySquare = !square?.ownerId && !square?.offlinePlayerId;
  const isOtherPlayerSquare = !isEmptySquare && !isOwnSquare;

  return (
    boardStatus !== 'locked' &&
    (
      (boardStatus === 'draft' && isManager && (
        assignModeEnabled
          ? !!(isSelectedPlayersSquare || (isEmptySquare && !assignModeAtLimit))
          : true
      )) ||
      (boardStatus === 'live' && (
        (isManager && (
          assignModeEnabled
            ? !!(isSelectedPlayersSquare || (isEmptySquare && !assignModeAtLimit))
            : true
        )) ||
        (!isManager && !isOtherPlayerSquare && (
          (isOwnSquare && !!onClearSquare) ||
          (isEmptySquare && !!onSquareClick && canClaimMore)
        ))
      ))
    )
  );
};

/**
 * Get display label for a square (icon + handle text).
 * Works for both standard (fixed icon size) and merged (scaled icon size) modes.
 * When showUnclaimedLabel is true, shows the unclaimedLabel for empty/undefined squares
 * (used in merged cell mode where cell numbers are always visible).
 */
export const getSquareLabel = (
  square: SquareWithNumbers | undefined,
  unclaimedLabel: string,
  hideNames: boolean,
  iconSize: number = 18,
  showUnclaimedLabel: boolean = false,
): { icon?: React.ReactNode; handle?: string } => {
  if (!square) return showUnclaimedLabel ? { handle: unclaimedLabel } : {};
  if (square.label) return { handle: square.label };

  if (hideNames && (square.offlinePlayer || square.owner || square.ownerId || square.offlinePlayerId)) {
    return {};
  }

  if (square.offlinePlayer) {
    const icon = square.offlinePlayer.iconName ? renderIcon(square.offlinePlayer.iconName, iconSize) : null;
    const handle = square.offlinePlayer.handle?.toUpperCase() || square.offlinePlayer.displayName.substring(0, 3).toUpperCase();
    return { icon: icon || undefined, handle };
  }

  if (square.owner) {
    const icon = square.owner.iconName ? renderIcon(square.owner.iconName, iconSize) : null;
    const handle = square.owner.handle?.toUpperCase() || square.owner.displayName.substring(0, 3).toUpperCase();
    return { icon: icon || undefined, handle };
  }

  return { handle: unclaimedLabel };
};

/**
 * Get tooltip text for a square.
 */
export const getSquareTooltip = (
  square: SquareWithNumbers | undefined,
  hitCount: number,
  hideNames: boolean,
): string => {
  if (hideNames && (square?.offlinePlayer || square?.owner || square?.ownerId || square?.offlinePlayerId)) {
    return 'Claimed';
  }

  const getName = () => {
    if (square?.offlinePlayer) {
      const handle = square.offlinePlayer.handle ? ` (@${square.offlinePlayer.handle.toUpperCase()})` : '';
      return `${square.offlinePlayer.displayName}${handle} (offline)`;
    }
    if (square?.owner) {
      const handle = square.owner.handle ? ` (@${square.owner.handle.toUpperCase()})` : '';
      return `${square.owner.displayName}${handle}`;
    }
    return 'Unknown';
  };

  if (hitCount > 0) {
    return `${getName()} - ${hitCount} win${hitCount > 1 ? 's' : ''}`;
  }
  if (square?.offlinePlayer) {
    const handle = square.offlinePlayer.handle ? ` (@${square.offlinePlayer.handle.toUpperCase()})` : '';
    return `${square.offlinePlayer.displayName}${handle} (offline)`;
  }
  if (square?.owner) {
    const handle = square.owner.handle ? ` (@${square.owner.handle.toUpperCase()})` : '';
    return `${square.owner.displayName}${handle}`;
  }
  if (square?.ownerId || square?.offlinePlayerId) {
    return 'Claimed';
  }
  return '';
};

/**
 * Winner hit count badge overlay.
 */
export const HitBadge: React.FC<{ count: number; themeData: ThemeData; size?: 'small' | 'large' }> = ({
  count,
  themeData,
  size = 'small',
}) => {
  if (count <= 0) return null;
  const isLarge = size === 'large';
  return (
    <Box
      sx={{
        position: 'absolute',
        top: isLarge ? 4 : 2,
        right: isLarge ? 4 : 2,
        backgroundColor: themeData.winnerBadgeBackground,
        color: themeData.winnerBadgeTextColor,
        borderRadius: '50%',
        width: isLarge ? 20 : 16,
        height: isLarge ? 20 : 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: isLarge ? '0.7rem' : '0.6rem',
        fontWeight: 'bold',
        boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
      }}
    >
      {count}
    </Box>
  );
};

/**
 * Bad number indicator badge overlay.
 */
export const BadNumberBadge: React.FC<{ isBad: boolean; size?: 'small' | 'large' }> = ({
  isBad,
  size = 'small',
}) => {
  if (!isBad) return null;
  const isLarge = size === 'large';
  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: isLarge ? 4 : 2,
        left: isLarge ? 4 : 2,
        backgroundColor: '#4caf50',
        color: '#fff',
        borderRadius: '50%',
        width: isLarge ? 18 : 14,
        height: isLarge ? 18 : 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: isLarge ? '0.7rem' : '0.6rem',
        fontWeight: 'bold',
        boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
      }}
      title="Bad Number - Automatic Winner!"
    >
      ✓
    </Box>
  );
};

/**
 * Cell content (icon + handle text).
 */
export const CellContent: React.FC<{
  icon?: React.ReactNode;
  handle?: string;
  textColor: string;
  gap?: number;
  fontSize: { withIcon: string; withoutIcon: string };
}> = ({ icon, handle, textColor, gap = 0, fontSize }) => (
  <Box
    sx={{
      color: textColor,
      fontWeight: 'bold',
      textAlign: 'center',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap,
      lineHeight: 1,
    }}
  >
    {icon && <Box sx={{ display: 'flex' }}>{icon}</Box>}
    {handle && (
      <Typography
        variant="caption"
        sx={{
          fontSize: icon ? fontSize.withIcon : fontSize.withoutIcon,
          fontWeight: 'bold',
          lineHeight: 1,
          color: 'inherit',
        }}
      >
        {handle}
      </Typography>
    )}
  </Box>
);

/**
 * Shared grid cell Paper wrapper with highlight/dim/hover styles.
 */
export const GridCell: React.FC<{
  width: number;
  height: number;
  bgColor: string;
  isClickable: boolean;
  isHighlighted: boolean;
  isDimmed: boolean;
  highlightScale: string;
  hoverScale: string;
  extraSx?: Record<string, unknown>;
  onClick: () => void;
  onMouseEnter: () => void;
  children: React.ReactNode;
}> = ({
  width,
  height,
  bgColor,
  isClickable,
  isHighlighted,
  isDimmed,
  highlightScale,
  hoverScale,
  extraSx = {},
  onClick,
  onMouseEnter,
  children,
}) => (
  <Paper
    elevation={isHighlighted ? 6 : 1}
    onClick={() => isClickable && onClick()}
    onMouseEnter={onMouseEnter}
    sx={{
      width,
      height,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: bgColor,
      cursor: isClickable ? 'pointer' : 'default',
      ml: 0.5,
      transition: 'transform 0.15s, box-shadow 0.15s, outline 0.15s, opacity 0.15s, filter 0.15s',
      position: 'relative',
      transform: isHighlighted ? `scale(${highlightScale})` : 'none',
      zIndex: isHighlighted ? 10 : 'auto',
      outline: isHighlighted ? '3px solid #ffd700' : 'none',
      outlineOffset: isHighlighted ? '-1px' : '0',
      opacity: isDimmed ? 0.35 : 1,
      filter: isDimmed ? 'grayscale(50%)' : 'none',
      borderRadius: 1,
      ...extraSx,
      '&:hover': isClickable
        ? { transform: `scale(${hoverScale})`, boxShadow: 3 }
        : {},
    }}
  >
    {children}
  </Paper>
);

/**
 * Wraps content in a Tooltip if tooltipText is non-empty, otherwise wraps in a Fragment.
 */
export const MaybeTooltip: React.FC<{
  tooltipText: string;
  reactKey: number;
  children: React.ReactElement;
}> = ({ tooltipText, reactKey, children }) => {
  if (tooltipText) {
    return (
      <Tooltip key={reactKey} title={tooltipText}>
        {children}
      </Tooltip>
    );
  }
  return <React.Fragment key={reactKey}>{children}</React.Fragment>;
};

/**
 * Shared number header cell (single number).
 */
export const NumberHeaderCell: React.FC<{
  num: number | string;
  isRevealed: boolean;
  isHighlighted: boolean;
  color: string;
  accentColor: string;
  themeData: ThemeData;
  width: number;
  height: number;
  direction?: 'horizontal' | 'vertical';
  isActive?: boolean;
}> = ({
  num,
  isRevealed,
  isHighlighted,
  color,
  accentColor,
  themeData,
  width,
  height,
  isActive = true,
}) => (
  <Box
    sx={{
      width,
      height,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: isHighlighted ? color : (isActive ? themeData.headerBackground : '#f5f5f5'),
      border: `1px solid ${themeData.cellBorder}`,
      color: isHighlighted ? accentColor : (isActive ? color : '#999'),
      fontWeight: themeData.headerFontWeight,
      fontFamily: themeData.fontFamily,
      fontSize: '0.85rem',
      transition: 'background-color 0.15s, color 0.15s, opacity 0.15s',
      opacity: isActive ? 1 : 0.5,
    }}
  >
    {isRevealed ? num : '-'}
  </Box>
);
